import { create } from 'zustand';
import type { AppState, SKU, DemandRecord, Zone, Location, MoveTask, ABCConfig, XYZConfig, SlottingRule, OptimizationConfig, ValidationError, CombinedClassification, SavedSession } from '@slot-matrix/shared-types';
import { VelocityMetric, PeriodGranularity, ZoneType, PickHeight, LifecycleStatus } from '@slot-matrix/shared-types';
import {
  classifyABC,
  classifyXYZ,
  aggregateDemand,
  forecast,
  buildMatrix,
  optimizeSlotting,
  calculateKPIs,
  applySeasonalUplift,
  seasonalPeakProximity,
  findSubstituteSeparations,
  runBenchmark,
  decomposeSeasonal,
  projectSeasonal,
} from '@slot-matrix/shared-logic';
import { loadTheme, saveTheme } from '../persistence/db';

// ── Default configs ──────────────────────────────────────────────────────────

const defaultABCConfig: ABCConfig = {
  metric: VelocityMetric.PickFrequency,
  thresholdA: 20,
  thresholdB: 30,
};

const defaultXYZConfig: XYZConfig = {
  thresholdXY: 0.5,
  thresholdYZ: 1.0,
  periodGranularity: PeriodGranularity.Monthly,
  lookbackDays: 365,
  useRecencyWeighting: false,
  recencyDecayFactor: 0.01,
  fixedPeriodMode: 'rolling',
  customPeriodStart: null,
  customPeriodEnd: null,
};

const defaultRules: SlottingRule[] = [
  { id: 'r1', segment: 'AX', targetZoneType: ZoneType.GoldenZone, targetPickHeight: PickHeight.Shoulder, priority: 1, constraints: [] },
  { id: 'r2', segment: 'AY', targetZoneType: ZoneType.GoldenZone, targetPickHeight: PickHeight.Waist, priority: 2, constraints: [] },
  { id: 'r3', segment: 'AZ', targetZoneType: ZoneType.ForwardPick, targetPickHeight: PickHeight.Waist, priority: 3, constraints: [] },
  { id: 'r4', segment: 'BX', targetZoneType: ZoneType.ForwardPick, targetPickHeight: PickHeight.Shoulder, priority: 4, constraints: [] },
  { id: 'r5', segment: 'BY', targetZoneType: ZoneType.ForwardPick, targetPickHeight: PickHeight.Waist, priority: 5, constraints: [] },
  { id: 'r6', segment: 'BZ', targetZoneType: ZoneType.Reserve, targetPickHeight: PickHeight.Waist, priority: 6, constraints: [] },
  { id: 'r7', segment: 'CX', targetZoneType: ZoneType.Reserve, targetPickHeight: PickHeight.Waist, priority: 7, constraints: [] },
  { id: 'r8', segment: 'CY', targetZoneType: ZoneType.Reserve, targetPickHeight: PickHeight.Floor, priority: 8, constraints: [] },
  { id: 'r9', segment: 'CZ', targetZoneType: ZoneType.Bulk, targetPickHeight: PickHeight.Floor, priority: 9, constraints: [] },
];

const defaultOptimizationConfig: OptimizationConfig = {
  rules: defaultRules,
  enableAffinityAnalysis: false,
  affinityThreshold: 5,
  objectiveWeights: {
    minimizeTravel: 0.4,
    maximizePickRate: 0.3,
    minimizeReplenishment: 0.15,
    maximizeSpaceUtil: 0.15,
  },
};

// ── Initial state ────────────────────────────────────────────────────────────

const initialState: AppState = {
  skus: [],
  demandRecords: [],
  abcResults: [],
  xyzResults: [],
  combinedClassifications: [],
  matrixCells: [],
  abcConfig: defaultABCConfig,
  xyzConfig: defaultXYZConfig,
  zones: [],
  locations: [],
  rules: defaultRules,
  optimizationConfig: defaultOptimizationConfig,
  slotAssignments: [],
  moveTasks: [],
  kpiData: null,
  activeScreen: 'analysis',
  theme: loadTheme(),
  settingsOpen: false,
  importWizardOpen: false,
  helpOpen: false,
  demoOpen: false,
  showLoadDemo: true,
  selectedSkuId: null,
  skuDrawerOpen: false,
  sessions: [],
  currentSessionId: null,
  currentSessionName: 'Untitled Session',
  importStep: 0,
  importType: null,
  importData: [],
  importMappings: [],
  importErrors: [],
  // Analytics slice
  historicalClassifications: [],
  scenarioList: [],
  selectedScenarioPair: null,
  sensitivityResults: [],
  opportunities: [],
  // Execution slice
  taskStatuses: {},
  skipReasons: {},
  claimedBy: {},
  // Alert slice
  alerts: [],
  // Audit slice
  auditLog: [],
  // Lifecycle / seasonal derived flags (emergent features)
  clearanceFlags: [],
  seasonalUpliftSkuIds: new Set<string>(),
};

// ── Store actions ────────────────────────────────────────────────────────────

interface Actions {
  // SKU
  setSkus: (skus: SKU[]) => void;
  addDemandRecords: (records: DemandRecord[]) => void;

  // Classification
  setAbcConfig: (config: Partial<ABCConfig>) => void;
  setXyzConfig: (config: Partial<XYZConfig>) => void;
  runClassification: () => void;

  // Warehouse
  addZone: (zone: Zone) => void;
  setLocations: (locations: Location[]) => void;
  addLocation: (location: Location) => void;

  // Slotting
  setOptimizationConfig: (config: Partial<OptimizationConfig>) => void;
  runOptimization: () => void;

  // UI
  setActiveScreen: (screen: string) => void;
  setTheme: (theme: 'light' | 'dark') => void;
  toggleTheme: () => void;
  setSettingsOpen: (open: boolean) => void;
  setImportWizardOpen: (open: boolean) => void;
  setHelpOpen: (open: boolean) => void;
  setDemoOpen: (open: boolean) => void;
  setShowLoadDemo: (show: boolean) => void;
  setSelectedSkuId: (id: string | null) => void;
  setSkuDrawerOpen: (open: boolean) => void;

  // Session
  setCurrentSessionName: (name: string) => void;

  // Import
  setImportStep: (step: number) => void;
  setImportType: (type: 'sku' | 'demand' | 'layout' | null) => void;
  setImportData: (data: Record<string, string>[]) => void;
  setImportErrors: (errors: ValidationError[]) => void;

  // Data management
  loadSampleData: () => void;
  loadDemoDataset: (datasetId: string) => void;
  resetAllData: () => void;

  // Analytics
  setScenarioList: (scenarios: SavedSession[]) => void;
  // Execution
  claimTask: (taskId: string) => void;
  completeTask: (taskId: string) => void;
  skipTask: (taskId: string, reason: string) => void;
  // Alert
  dismissAlert: (alertId: string) => void;
  // Audit
  clearAuditLog: () => void;
}

export type AppStore = AppState & Actions;

export const useAppStore = create<AppStore>()((set, get) => ({
  ...initialState,

  // ── SKU actions ──────────────────────────────────────────────────────
  setSkus: (skus) => set({ skus }),
  addDemandRecords: (records) =>
    set((s) => ({ demandRecords: [...s.demandRecords, ...records] })),

  // ── Classification actions ──────────────────────────────────────────
  setAbcConfig: (config) => set((s) => ({ abcConfig: { ...s.abcConfig, ...config } })),
  setXyzConfig: (config) => set((s) => ({ xyzConfig: { ...s.xyzConfig, ...config } })),

  runClassification: () => {
    const state = get();
    if (state.skus.length === 0) return;

    // Build metric values from demand records
    const metricValues = new Map<string, number>();
    if (state.abcConfig.metric === VelocityMetric.PickFrequency) {
      // Count distinct dates with demand per SKU
      const skuDates = new Map<string, Set<string>>();
      for (const rec of state.demandRecords) {
        if (!skuDates.has(rec.skuId)) skuDates.set(rec.skuId, new Set());
        skuDates.get(rec.skuId)!.add(rec.date);
      }
      for (const sku of state.skus) {
        metricValues.set(sku.id, skuDates.get(sku.id)?.size ?? 0);
      }
    } else {
      // Sum quantity per SKU
      for (const rec of state.demandRecords) {
        metricValues.set(rec.skuId, (metricValues.get(rec.skuId) ?? 0) + rec.quantity);
      }
    }

    // ABC classification
    const abcResults = classifyABC(state.skus, metricValues, state.abcConfig);

    // XYZ classification
    const timeSeries = aggregateDemand(state.demandRecords, state.xyzConfig.periodGranularity);
    const xyzResults = classifyXYZ(timeSeries, state.xyzConfig);

    // Build combined classifications
    const xyzMap = new Map(xyzResults.map((r) => [r.skuId, r]));
    const combinedClassifications: CombinedClassification[] = abcResults.map((abc) => {
      const xyz = xyzMap.get(abc.skuId);
      return {
        skuId: abc.skuId,
        abc,
        xyz: xyz ?? {
          skuId: abc.skuId,
          meanDemand: 0,
          stdDev: 0,
          cv: NaN,
          periodCount: 0,
          zeroDemandPeriods: 0,
          isIntermittent: false,
          assignedClass: 'Z' as any,
          excludedAnomalyIndices: [],
        },
        segment: `${abc.assignedClass}${xyz?.assignedClass ?? 'Z'}`,
      };
    });

    // Build matrix
    const matrixCells = buildMatrix(combinedClassifications, abcResults, xyzResults);

    set({ abcResults, xyzResults, combinedClassifications, matrixCells,
      historicalClassifications: [...get().historicalClassifications, combinedClassifications] });
  },

  // ── Warehouse actions ───────────────────────────────────────────────
  addZone: (zone) => set((s) => ({ zones: [...s.zones, zone] })),
  setLocations: (locations) => set({ locations }),
  addLocation: (location) => set((s) => ({ locations: [...s.locations, location] })),

  // ── Slotting actions ────────────────────────────────────────────────
  setOptimizationConfig: (config) =>
    set((s) => ({ optimizationConfig: { ...s.optimizationConfig, ...config } })),

  runOptimization: () => {
    const state = get();
    if (state.skus.length === 0 || state.locations.length === 0) return;

    // Ensure classification has been run
    if (state.combinedClassifications.length === 0) return;

    // Apply seasonal uplift before slotting (FE-E1)
    const uplift = applySeasonalUplift(
      state.skus,
      state.combinedClassifications,
      state.demandRecords,
    );
    const segments = uplift.segments;

    const assignments = optimizeSlotting(
      state.skus,
      state.locations,
      state.zones,
      state.rules,
      state.optimizationConfig,
      segments,
    );

    // Apply lifecycle rules when building move tasks (FE-E2)
    const skuById = new Map(state.skus.map((s) => [s.id, s]));
    const clearanceFlags: string[] = [];
    for (const a of assignments) {
      const sku = skuById.get(a.skuId);
      if (!sku) continue;
      if (sku.lifecycleStatus === LifecycleStatus.NewItem && !a.currentLocationId) {
        a.rationale.push('Trial placement (new item)');
      }
      if (sku.lifecycleStatus === LifecycleStatus.Discontinued) {
        clearanceFlags.push(sku.id);
      }
    }

    // Generate move tasks — skip discontinued SKUs, halve impact for phase-out
    const moveTasks: MoveTask[] = assignments
      .filter((a) => {
        const sku = skuById.get(a.skuId);
        if (sku?.lifecycleStatus === LifecycleStatus.Discontinued) return false;
        return a.currentLocationId !== a.recommendedLocationId || !a.currentLocationId;
      })
      .map((a, i) => {
        const sku = skuById.get(a.skuId);
        let impactScore = a.impactScore;
        if (sku?.lifecycleStatus === LifecycleStatus.PhaseOut) {
          impactScore = Math.round(impactScore / 2);
          a.rationale.push('Phase-out: deprioritized');
        }
        return {
          id: `move-${i + 1}`,
          skuId: a.skuId,
          fromLocationId: a.currentLocationId,
          toLocationId: a.recommendedLocationId,
          priority: a.movePriority,
          wave: Math.ceil((i + 1) / 10),
          impactScore,
          status: 'pending' as const,
        };
      });

    // Calculate KPIs
    const kpiData = calculateKPIs(
      assignments,
      state.skus,
      state.locations,
      state.zones,
      segments,
    );

    set({
      slotAssignments: assignments,
      moveTasks,
      kpiData,
      clearanceFlags,
      seasonalUpliftSkuIds: uplift.upliftedSkuIds,
    });
  },

  // ── UI actions ──────────────────────────────────────────────────────
  setActiveScreen: (screen) => set({ activeScreen: screen }),
  setTheme: (theme) => {
    saveTheme(theme);
    document.documentElement.setAttribute('data-theme', theme);
    set({ theme });
  },
  toggleTheme: () => {
    const newTheme = get().theme === 'light' ? 'dark' : 'light';
    saveTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    set({ theme: newTheme });
  },
  setSettingsOpen: (open) => set({ settingsOpen: open }),
  setImportWizardOpen: (open) => set({ importWizardOpen: open }),
  setHelpOpen: (open) => set({ helpOpen: open }),
  setDemoOpen: (open) => set({ demoOpen: open }),
  setShowLoadDemo: (show) => set({ showLoadDemo: show }),
  setSelectedSkuId: (id) => set({ selectedSkuId: id }),
  setSkuDrawerOpen: (open) => set({ skuDrawerOpen: open }),

  // ── Session actions ─────────────────────────────────────────────────
  setCurrentSessionName: (name) => set({ currentSessionName: name }),

  // ── Import actions ──────────────────────────────────────────────────
  setImportStep: (step) => set({ importStep: step }),
  setImportType: (type) => set({ importType: type }),
  setImportData: (data) => set({ importData: data }),
  setImportErrors: (errors) => set({ importErrors: errors }),

  // ── Data management ─────────────────────────────────────────────────
  loadSampleData: () => {
    // Dynamic import to avoid bundling sample data unless needed
    import('../data/sampleData').then(({ sampleSkus, sampleDemandRecords, sampleZones, sampleLocations }) => {
      set({
        skus: sampleSkus,
        demandRecords: sampleDemandRecords,
        zones: sampleZones,
        locations: sampleLocations,
        abcResults: [],
        xyzResults: [],
        combinedClassifications: [],
        matrixCells: [],
        slotAssignments: [],
        moveTasks: [],
        kpiData: null,
        clearanceFlags: [],
        seasonalUpliftSkuIds: new Set<string>(),
      });
      // Auto-run classification
      get().runClassification();
    });
  },

  loadDemoDataset: (datasetId) => {
    import('../data/demoData').then(({ demoDatasets }) => {
      const ds = demoDatasets.find((d) => d.id === datasetId);
      if (!ds) return;
      set({
        skus: ds.skus,
        demandRecords: ds.demandRecords,
        zones: ds.zones,
        locations: ds.locations,
        rules: ds.rules,
        abcResults: [],
        xyzResults: [],
        combinedClassifications: [],
        matrixCells: [],
        slotAssignments: [],
        moveTasks: [],
        kpiData: null,
        historicalClassifications: [],
        sensitivityResults: [],
        opportunities: [],
        alerts: [],
        auditLog: [],
        taskStatuses: {},
        skipReasons: {},
        claimedBy: {},
        clearanceFlags: [],
        seasonalUpliftSkuIds: new Set<string>(),
      });
      get().runClassification();
    });
  },

  resetAllData: () =>
    set({
      skus: [],
      demandRecords: [],
      abcResults: [],
      xyzResults: [],
      combinedClassifications: [],
      matrixCells: [],
      slotAssignments: [],
      moveTasks: [],
      kpiData: null,
      historicalClassifications: [],
      sensitivityResults: [],
      opportunities: [],
      clearanceFlags: [],
      seasonalUpliftSkuIds: new Set<string>(),
    }),

  // ── Analytics actions ──────────────────────────────────────────────
  setScenarioList: (scenarios) => set({ scenarioList: scenarios }),

  // ── Execution actions ──────────────────────────────────────────────
  claimTask: (taskId) =>
    set((s) => ({
      taskStatuses: { ...s.taskStatuses, [taskId]: 'in_progress' },
      claimedBy: { ...s.claimedBy, [taskId]: 'current-user' },
      auditLog: [...s.auditLog, { timestamp: new Date().toISOString(), action: 'claim', entityType: 'task', entityId: taskId, userId: 'current-user', details: 'Task claimed' }],
    })),

  completeTask: (taskId) =>
    set((s) => ({
      taskStatuses: { ...s.taskStatuses, [taskId]: 'complete' },
      auditLog: [...s.auditLog, { timestamp: new Date().toISOString(), action: 'complete', entityType: 'task', entityId: taskId, userId: 'current-user', details: 'Task completed' }],
    })),

  skipTask: (taskId, reason) =>
    set((s) => ({
      taskStatuses: { ...s.taskStatuses, [taskId]: 'skipped' },
      skipReasons: { ...s.skipReasons, [taskId]: reason },
      auditLog: [...s.auditLog, { timestamp: new Date().toISOString(), action: 'skip', entityType: 'task', entityId: taskId, userId: 'current-user', details: `Skipped: ${reason}` }],
    })),

  // ── Alert actions ──────────────────────────────────────────────────
  dismissAlert: (alertId) =>
    set((s) => ({
      alerts: s.alerts.map((a) => (a.id === alertId ? { ...a, dismissed: true } : a)),
    })),

  // ── Audit actions ──────────────────────────────────────────────────
  clearAuditLog: () => set({ auditLog: [] }),
}));

// ── Selectors ────────────────────────────────────────────────────────────────

export const selectSkus = (state: AppState) => state.skus;
export const selectDemandRecords = (state: AppState) => state.demandRecords;
export const selectAbcResults = (state: AppState) => state.abcResults;
export const selectXyzResults = (state: AppState) => state.xyzResults;
export const selectMatrixCells = (state: AppState) => state.matrixCells;
export const selectAbcConfig = (state: AppState) => state.abcConfig;
export const selectXyzConfig = (state: AppState) => state.xyzConfig;
export const selectZones = (state: AppState) => state.zones;
export const selectLocations = (state: AppState) => state.locations;
export const selectRules = (state: AppState) => state.rules;
export const selectOptimizationConfig = (state: AppState) => state.optimizationConfig;
export const selectSlotAssignments = (state: AppState) => state.slotAssignments;
export const selectMoveTasks = (state: AppState) => state.moveTasks;
export const selectKpiData = (state: AppState) => state.kpiData;
export const selectActiveScreen = (state: AppState) => state.activeScreen;
export const selectTheme = (state: AppState) => state.theme;
export const selectShowLoadDemo = (state: AppState) => state.showLoadDemo;

// ── Analytics selectors ──────────────────────────────────────────────────

let _excCache: { key: string; value: { skuId: string; type: string; message: string }[] } | null = null;
export const selectExceptions = (state: AppState) => {
  const cacheKey = `${state.skus.length}:${state.slotAssignments.length}:${state.combinedClassifications.length}:${state.rules.length}`;
  if (_excCache && _excCache.key === cacheKey) return _excCache.value;
  const exceptions: { skuId: string; type: string; message: string }[] = [];
  const assignedIds = new Set(state.slotAssignments.map((a) => a.skuId));
  for (const sku of state.skus) {
    if (!assignedIds.has(sku.id)) {
      exceptions.push({ skuId: sku.id, type: 'unassigned', message: `${sku.description} has no slot assignment` });
    }
  }
  for (const a of state.slotAssignments) {
    for (const v of a.constraintViolations) {
      exceptions.push({ skuId: a.skuId, type: 'constraint', message: v });
    }
  }
  const segments = new Set(state.rules.map((r) => r.segment));
  for (const cc of state.combinedClassifications) {
    if (!segments.has(cc.segment)) {
      exceptions.push({ skuId: cc.skuId, type: 'no_rule', message: `No rule for segment ${cc.segment}` });
    }
  }
  _excCache = { key: cacheKey, value: exceptions };
  return exceptions;
};

const _emptyEvo: never[] = [];
let _evoCache: { key: number; value: { runIndex: number; A: number; B: number; C: number }[] } | null = null;
export const selectDistributionEvolution = (state: AppState) => {
  if (state.historicalClassifications.length === 0) return _emptyEvo;
  const key = state.historicalClassifications.length;
  if (_evoCache && _evoCache.key === key) return _evoCache.value;
  const value = state.historicalClassifications.map((run, idx) => {
    const dist = { A: 0, B: 0, C: 0 };
    for (const c of run) {
      dist[c.abc.assignedClass as 'A' | 'B' | 'C']++;
    }
    return { runIndex: idx, ...dist };
  });
  _evoCache = { key, value };
  return value;
};

export const selectSensitivityResults = (state: AppState) => state.sensitivityResults;
export const selectOpportunities = (state: AppState) => state.opportunities;
export const selectScenarioList = (state: AppState) => state.scenarioList;
export const selectClearanceFlags = (state: AppState) => state.clearanceFlags;
export const selectSeasonalUpliftSkuIds = (state: AppState) => state.seasonalUpliftSkuIds;

export interface BenchmarkPrescription {
  metric: string;
  rating: string;
  prescriptions: { kind: string; skuIds: string[]; text: string }[];
}

const EMPTY_BENCH_PRESCRIPTIONS: BenchmarkPrescription[] = [];
let _benchCache: { key: string; value: BenchmarkPrescription[] } | null = null;
export const selectBenchmarkPrescriptions = (state: AppState): BenchmarkPrescription[] => {
  const kpi = state.kpiData;
  const cacheKey = `${kpi ? JSON.stringify(kpi) : ''}:${state.slotAssignments.length}:${state.locations.length}:${state.zones.length}`;
  if (_benchCache && _benchCache.key === cacheKey) return _benchCache.value;

  if (!kpi) {
    _benchCache = { key: cacheKey, value: EMPTY_BENCH_PRESCRIPTIONS };
    return EMPTY_BENCH_PRESCRIPTIONS;
  }

  const benchmarks = runBenchmark({
    alignmentScore: kpi.alignmentScore,
    travelReduction: kpi.travelReduction,
    goldenZoneUtilization: kpi.goldenZoneUtilization,
    spaceUtilization: kpi.spaceUtilization,
  });

  const locById = new Map(state.locations.map((l) => [l.id, l]));
  const zoneById = new Map(state.zones.map((z) => [z.id, z]));
  const isGolden = (locId: string) => {
    const loc = locById.get(locId);
    if (!loc) return false;
    return zoneById.get(loc.zoneId)?.type === ZoneType.GoldenZone;
  };

  const result: BenchmarkPrescription[] = [];

  for (const b of benchmarks) {
    if (b.rating === 'excellent' || b.rating === 'good') continue;
    const prescriptions: { kind: string; skuIds: string[]; text: string }[] = [];

    if (b.metric === 'goldenZoneUtilization') {
      const offenders = state.slotAssignments
        .filter((a) => a.segment.startsWith('A') && !isGolden(a.recommendedLocationId))
        .sort((x, y) => y.impactScore - x.impactScore)
        .slice(0, 3)
        .map((a) => a.skuId);
      prescriptions.push({ kind: 'golden_zone', skuIds: offenders, text: 'Move these A-items into the golden zone' });
    } else if (b.metric === 'alignmentScore') {
      const low = state.slotAssignments.filter((a) => a.impactScore < 50);
      const sample = low.slice(0, 3).map((a) => a.skuId);
      prescriptions.push({ kind: 'alignment', skuIds: sample, text: `${low.length} SKUs have low impact scores; review placement` });
    } else if (b.metric === 'spaceUtilization') {
      const lowZones = state.zones.filter((z) => {
        const locs = state.locations.filter((l) => l.zoneId === z.id);
        if (locs.length === 0) return false;
        const used = locs.filter((l) =>
          state.slotAssignments.some((a) => a.recommendedLocationId === l.id),
        ).length;
        return (used / locs.length) * 100 < 40;
      });
      prescriptions.push({ kind: 'space', skuIds: lowZones.map((z) => z.id), text: 'Relocate reserve/bulk items into underused zones' });
    } else if (b.metric === 'travelReduction') {
      const top = [...state.slotAssignments]
        .sort((x, y) => y.travelReduction - x.travelReduction)
        .slice(0, 3)
        .filter((a) => a.travelReduction > 0)
        .map((a) => a.skuId);
      prescriptions.push({ kind: 'travel', skuIds: top, text: 'Prioritize these high-travel-reduction moves' });
    }

    result.push({ metric: b.metric, rating: b.rating, prescriptions });
  }

  _benchCache = { key: cacheKey, value: result };
  return result;
};

let _subCache: { key: string; value: { skuId: string; substituteId: string; skuZoneId: string; subZoneId: string }[] } | null = null;
export const selectSubstituteSeparations = (state: AppState) => {
  const cacheKey = `${state.skus.length}:${state.slotAssignments.length}:${state.locations.length}:${state.zones.length}`;
  if (_subCache && _subCache.key === cacheKey) return _subCache.value;
  const value = findSubstituteSeparations(state.skus, state.slotAssignments, state.zones, state.locations);
  _subCache = { key: cacheKey, value };
  return value;
};

const ABC_ORDER = ['A', 'B', 'C'];
export interface ForecastedClassChanges {
  promoters: { skuId: string; from: string; to: string }[];
  demoters: { skuId: string; from: string; to: string }[];
}

let _forecastCache: { key: string; value: ForecastedClassChanges } | null = null;
export const selectForecastedClassChanges = (state: AppState): ForecastedClassChanges => {
  const cacheKey = `${state.skus.length}:${state.demandRecords.length}:${state.abcResults.length}:${state.abcConfig.metric}`;
  if (_forecastCache && _forecastCache.key === cacheKey) return _forecastCache.value;

  const empty: ForecastedClassChanges = { promoters: [], demoters: [] };
  if (state.skus.length === 0 || state.abcResults.length === 0) {
    _forecastCache = { key: cacheKey, value: empty };
    return empty;
  }

  const periodsBySku = aggregateDemand(state.demandRecords, state.xyzConfig.periodGranularity);
  const periodsMap = new Map(periodsBySku.map((ts) => [ts.skuId, ts.periods]));

  const forecastedMetricValues = new Map<string, number>();
  for (const sku of state.skus) {
    const periods = periodsMap.get(sku.id);
    if (!periods || periods.length < 3) {
      forecastedMetricValues.set(sku.id, 0);
      continue;
    }
    const fc = forecast(sku.id, periods, 3);
    const sum = fc.forecast.reduce((a, b) => a + b, 0);
    forecastedMetricValues.set(sku.id, sum);
  }

  const forecastedResults = classifyABC(state.skus, forecastedMetricValues, state.abcConfig);
  const currentBySku = new Map(state.abcResults.map((r) => [r.skuId, r.assignedClass]));

  const promoters: { skuId: string; from: string; to: string }[] = [];
  const demoters: { skuId: string; from: string; to: string }[] = [];

  for (const r of forecastedResults) {
    const from = currentBySku.get(r.skuId);
    if (!from || from === r.assignedClass) continue;
    const fromIdx = ABC_ORDER.indexOf(from);
    const toIdx = ABC_ORDER.indexOf(r.assignedClass);
    if (toIdx < fromIdx) {
      promoters.push({ skuId: r.skuId, from, to: r.assignedClass });
    } else if (toIdx > fromIdx) {
      demoters.push({ skuId: r.skuId, from, to: r.assignedClass });
    }
  }

  const value = { promoters, demoters };
  _forecastCache = { key: cacheKey, value };
  return value;
};

export interface SeasonalRoiTiming {
  seasonalMoveCount: number;
  nearestPeakMonth: number;
  weeksUntilPeak: number;
  peakWindowShare: number;
  acceleratedPaybackWeeks: number;
}

const EMPTY_SEASONAL_TIMING = null;
let _seasonalRoiCache: { key: string; value: SeasonalRoiTiming | null } | null = null;

/**
 * Compute seasonal payback timing for the current move list.
 * `normalPayback` is the caller-provided ROI payback (weeks).
 * Returns null when no seasonal SKUs are involved in moves.
 */
export const selectSeasonalRoiTiming = (state: AppState, normalPayback: number): SeasonalRoiTiming | null => {
  const cacheKey = `${state.moveTasks.length}:${state.skus.length}:${state.demandRecords.length}:${normalPayback}`;
  if (_seasonalRoiCache && _seasonalRoiCache.key === cacheKey) return _seasonalRoiCache.value;

  const moveSkuIds = new Set(state.moveTasks.map((t) => t.skuId));
  const seasonalMoveSkus = state.skus.filter((s) => s.isSeasonal && moveSkuIds.has(s.id));

  if (seasonalMoveSkus.length === 0) {
    _seasonalRoiCache = { key: cacheKey, value: EMPTY_SEASONAL_TIMING };
    return EMPTY_SEASONAL_TIMING;
  }

  // Aggregate monthly demand per SKU
  const monthlyBySku = new Map<string, Map<string, number>>();
  for (const rec of state.demandRecords) {
    const monthKey = rec.date.slice(0, 7);
    if (!monthlyBySku.has(rec.skuId)) monthlyBySku.set(rec.skuId, new Map());
    const m = monthlyBySku.get(rec.skuId)!;
    m.set(monthKey, (m.get(monthKey) ?? 0) + rec.quantity);
  }

  const today = new Date();
  let nearestPeakMonth = 0;
  let nearestWeeks = Infinity;

  // First pass: find the nearest peak month across all seasonal move SKUs
  const seasonalPeriods = new Map<string, { periodStart: string; quantity: number }[]>();
  for (const sku of seasonalMoveSkus) {
    const monthly = monthlyBySku.get(sku.id);
    if (!monthly || monthly.size < 12) continue;
    const sortedMonths = Array.from(monthly.keys()).sort();
    const periods = sortedMonths.map((m) => ({ periodStart: `${m}-01`, quantity: monthly.get(m) ?? 0 }));
    const decomp = decomposeSeasonal(sku.id, periods, 12);
    seasonalPeriods.set(sku.id, periods);
    const proximity = seasonalPeakProximity(decomp.peakMonth, today);
    if (proximity.weeksUntilPeak < nearestWeeks) {
      nearestWeeks = proximity.weeksUntilPeak;
      nearestPeakMonth = decomp.peakMonth;
    }
  }

  if (nearestPeakMonth === 0 || !isFinite(nearestWeeks)) {
    _seasonalRoiCache = { key: cacheKey, value: EMPTY_SEASONAL_TIMING };
    return EMPTY_SEASONAL_TIMING;
  }

  // Second pass: compute peak-window demand share using full-year projections
  let peakWindowDemand = 0;
  let totalAnnualDemand = 0;
  for (const sku of seasonalMoveSkus) {
    const periods = seasonalPeriods.get(sku.id);
    if (!periods) continue;
    const projection = projectSeasonal(sku.id, periods, 12, 12);
    totalAnnualDemand += projection.projectedAnnual;
    for (let i = 0; i < projection.projections.length; i++) {
      const p = projection.projections[i];
      const calendarMonth = (periods.length + i) % 12 + 1; // 1-based month
      const distance = Math.min(
        Math.abs(calendarMonth - nearestPeakMonth),
        12 - Math.abs(calendarMonth - nearestPeakMonth),
      );
      if (distance <= 1) peakWindowDemand += p.projectedDemand;
    }
  }

  const peakWindowShare = totalAnnualDemand > 0 ? peakWindowDemand / totalAnnualDemand : 0;
  const acceleratedPaybackWeeks = Math.max(1, Math.round(normalPayback * (1 - peakWindowShare / 2)));

  const value: SeasonalRoiTiming = {
    seasonalMoveCount: seasonalMoveSkus.length,
    nearestPeakMonth,
    weeksUntilPeak: nearestWeeks,
    peakWindowShare: Math.round(peakWindowShare * 1000) / 1000,
    acceleratedPaybackWeeks,
  };
  _seasonalRoiCache = { key: cacheKey, value };
  return value;
};

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
